<script setup lang="ts">
import { reactiveOmit } from '@vueuse/core';
import type { CheckboxRootEmits, CheckboxRootProps } from 'radix-vue';
import { CheckboxIndicator, CheckboxRoot, useForwardPropsEmits } from 'radix-vue';
import Icon from 'shared/icons/check.svg';
import styles from './styles.module.css';

export interface BaseCheckboxProps extends CheckboxRootProps {
  label?: string;
  cb?: (v: boolean) => void;
  emit?: () => void;
}

const props = defineProps<BaseCheckboxProps>();
const emits = defineEmits<CheckboxRootEmits>();

defineSlots<{ default(props: object): never }>();

const checkboxRootProps = reactiveOmit(props, 'label');
const forwarded = useForwardPropsEmits(checkboxRootProps, emits);

function handleCheckboxChange(newValue: boolean): void {
  if (props.cb) {
    props.cb(newValue);
  }
  props.emit?.();
}
</script>

<template>
  <label :class="styles.checkbox">
    <CheckboxRoot v-bind="forwarded" :class="styles.root" @update:checked="handleCheckboxChange">
      <CheckboxIndicator :class="styles.indicator">
        <Icon />
      </CheckboxIndicator>
    </CheckboxRoot>

    <typography
      v-if="$slots.default"
      variant="body-2"
      :class="[styles.label, { [styles.disabled]: forwarded.disabled }]"
    >
      <slot />
    </typography>
  </label>
</template>
